/**
 * CREATIVE DETONATOR - Route Protection Guards
 * v2 - Uses centralized API_BASE from endpoints.js
 *
 * Utilities for protecting routes that require authentication.
 * SECURITY: Includes protection against open redirect attacks.
 */

import { isLoggedIn, getSessionToken } from './client.js';
import { API_BASE } from '../../../js/api/endpoints.js';

// ============================================================================
// SECURITY: Open Redirect Protection
// ============================================================================

/**
 * Validate redirect URL is safe (same origin only)
 * Prevents open redirect attacks
 * @param {string} url - URL to validate
 * @returns {boolean} - True if URL is safe to redirect to
 */
function isValidRedirect(url) {
  if (!url) return false;
  try {
    // Handle relative URLs
    const parsed = new URL(url, window.location.origin);
    // Only allow same-origin redirects
    return parsed.origin === window.location.origin;
  } catch {
    // Invalid URL - only allow path-like strings starting with /
    return typeof url === 'string' && url.startsWith('/') && !url.startsWith('//');
  }
}

/**
 * Get safe redirect URL, falling back to default if invalid
 * @param {string} url - URL to validate
 * @param {string} fallback - Fallback URL if invalid
 * @returns {string} - Safe URL to redirect to
 */
function getSafeRedirect(url, fallback = '/features/generator/index.html') {
  return isValidRedirect(url) ? url : fallback;
}

// ============================================================================
// AUTH GUARDS
// ============================================================================

/**
 * Require authentication - redirects to login if not logged in
 * Use this at the top of protected pages
 *
 * @param {string} loginUrl - URL to redirect to for login
 * @returns {Promise<boolean>} - true if authenticated, false if redirecting
 *
 * @example
 * // At top of generator.js
 * if (!await requireAuth()) return;
 */
export async function requireAuth(loginUrl = '/features/auth/pages/login.html') {
  const loggedIn = await isLoggedIn();

  if (!loggedIn) {
    // Save intended destination for redirect after login
    sessionStorage.setItem('redirectAfterLogin', window.location.href);
    window.location.href = loginUrl;
    return false;
  }

  return true;
}

/**
 * Redirect if already logged in (for login/signup pages)
 * Prevents logged-in users from seeing auth pages
 *
 * @param {string} destination - URL to redirect to if logged in
 * @returns {Promise<boolean>} - true if redirected, false if not logged in
 *
 * @example
 * // At top of login.js
 * if (await redirectIfLoggedIn()) return;
 */
export async function redirectIfLoggedIn(destination = '/features/generator/index.html') {
  const loggedIn = await isLoggedIn();

  if (loggedIn) {
    // Check for saved redirect destination with validation
    const savedRedirect = sessionStorage.getItem('redirectAfterLogin');
    sessionStorage.removeItem('redirectAfterLogin');
    // SECURITY: Validate redirect URL to prevent open redirect attacks
    const safeRedirect = getSafeRedirect(savedRedirect, destination);
    window.location.href = safeRedirect;
    return true;
  }

  return false;
}

/**
 * Require admin role - redirects to generator if not admin
 * Use this for admin-only pages
 *
 * @returns {Promise<boolean>} - true if admin, false if redirecting
 */
export async function requireAdmin() {
  const loggedIn = await isLoggedIn();

  if (!loggedIn) {
    sessionStorage.setItem('redirectAfterLogin', window.location.href);
    window.location.href = '/features/auth/pages/login.html';
    return false;
  }

  try {
    const token = await getSessionToken();
    const response = await fetch(`${API_BASE}/api/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const { profile } = await response.json();
      if (profile?.role !== 'admin') {
        // Not admin, redirect to generator
        window.location.href = '/features/generator/index.html';
        return false;
      }
    } else {
      // Failed to verify, redirect to login
      window.location.href = '/features/auth/pages/login.html';
      return false;
    }
  } catch (error) {
    console.error('[Guard] Admin check failed:', error);
    window.location.href = '/features/generator/index.html';
    return false;
  }

  return true;
}

/**
 * Check if user has sufficient credits
 * Does not redirect, just returns boolean
 *
 * @param {number} required - Credits required
 * @returns {Promise<{hasCredits: boolean, available: number}>}
 */
export async function checkCredits(required) {
  try {
    const token = await getSessionToken();
    const response = await fetch(`${API_BASE}/api/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const { profile } = await response.json();
      const available = profile?.credits || 0;
      return {
        hasCredits: available >= required,
        available
      };
    }
  } catch (error) {
    console.error('[Guard] Credit check failed:', error);
  }

  return { hasCredits: false, available: 0 };
}

/**
 * Get redirect URL after login
 * @returns {string|null}
 */
export function getRedirectAfterLogin() {
  return sessionStorage.getItem('redirectAfterLogin');
}

/**
 * Clear redirect URL
 */
export function clearRedirectAfterLogin() {
  sessionStorage.removeItem('redirectAfterLogin');
}
